import type { ContentSession } from '@/src/queries/library-queries'
import { Image as ExpoImage } from 'expo-image'
import { withTimeout } from '@/src/utils/promise-timeout'

import { buildImageCandidateUrls, normalizeMediaUrl } from './media'

const SESSION_MEDIA_PREFETCH_WAIT_LIMIT_MS = 10_000
const completedSessionPrefetches = new Set<string>()
const inFlightSessionPrefetches = new Map<string, Promise<void>>()

export function isSessionPrefetchComplete(sessionId: string): boolean {
  return completedSessionPrefetches.has(sessionId)
}

interface PrefetchSessionMediaOptions {
  maxWaitMs?: number
}

function uniqueUrls(urls: string[]): string[] {
  return [...new Set(urls)]
}

function collectSessionImageUrls(session: ContentSession): string[] {
  const imageUrls: string[] = []

  if (!session.steps.length) {
    return []
  }

  session.steps.forEach((step) => {
    step.media.forEach((asset) => {
      const normalizedUrl = normalizeMediaUrl(asset.url ?? undefined)
      const contentType = asset.contentType

      if (normalizedUrl == null) {
        return
      }

      const primaryUrl = buildImageCandidateUrls(normalizedUrl, contentType)[0]
      if (primaryUrl != null) imageUrls.push(primaryUrl)
    })
  })

  return uniqueUrls(imageUrls)
}

async function prefetchImages(imageUrls: string[]): Promise<void> {
  if (imageUrls.length === 0) {
    return
  }

  await Promise.allSettled(imageUrls.map((url) => ExpoImage.prefetch(url)))
}

function withWaitLimit(
  prefetchPromise: Promise<void>,
  maxWaitMs: number,
): Promise<void> {
  if (maxWaitMs <= 0) {
    return Promise.resolve()
  }

  return withTimeout(prefetchPromise, {
    timeoutMs: maxWaitMs,
    onTimeout: () => {
      return
    },
  })
}

export function prefetchSessionMedia(
  session: ContentSession,
  options?: PrefetchSessionMediaOptions,
): Promise<void> {
  const maxWaitMs = options?.maxWaitMs ?? SESSION_MEDIA_PREFETCH_WAIT_LIMIT_MS
  const sessionId = session.id

  if (completedSessionPrefetches.has(sessionId)) {
    return Promise.resolve()
  }

  const inFlight = inFlightSessionPrefetches.get(sessionId)
  if (inFlight != null) {
    return withWaitLimit(inFlight, maxWaitMs)
  }

  const imageUrls = collectSessionImageUrls(session)

  const prefetchPromise = prefetchImages(imageUrls)
    .then(() => {
      completedSessionPrefetches.add(sessionId)
    })
    .finally(() => {
      inFlightSessionPrefetches.delete(sessionId)
    })

  inFlightSessionPrefetches.set(sessionId, prefetchPromise)

  return withWaitLimit(prefetchPromise, maxWaitMs)
}
